import { useMemo, useCallback, useEffect } from "react";
import { useLocation, useNavigate } from "react-router";
import {
  AssistantWorkspace,
  buildAssistantAppState,
  useChat,
  useConversationSession,
  useConversations,
  useDeleteConversation,
} from "@bastion/assistant";
import {
  DashboardArtifactPanel,
  type Dashboard,
  type DashboardDefinition,
  type DashboardWidget,
} from "@bastion/dashboards";
import { useDashboardAssistantContextStore, useEpisodeDisplayStore, useEpisodeFiltersStore } from "@bastion/stores";
import { mainChatSessionIdRef, routeToTabId } from "@/app/assistantTools";
import { flattenEpisodeFiltersForAssistant } from "@bastion/episodes";
import { readEpisodeFiltersFromSearch } from "@/app/layout/readEpisodeFilters";
import { readWorkflowFiltersFromLocation } from "@/app/layout/readWorkflowFilters";
import styles from "./WebAssistantWorkspace.module.css";

interface WebAssistantWorkspaceProps {
  sessionId?: string | null;
  initialMessage?: string;
}

const CAPABILITY_PROMPTS = [
  {
    label: "Build me a dashboard",
    prompt:
      "I want to create a dashboard. Ask me what it should cover, then explore the schema and stream widgets into the preview.",
  },
  {
    label: "Summarize recent data",
    prompt:
      "Summarize recent robot data collection volume and quality. Call out notable operators, days, or quality patterns.",
  },
  {
    label: "Find weak spots",
    prompt:
      "Analyze recent robot episodes and tell me where data quality, task ratings, or collection coverage look weakest.",
  },
  {
    label: "Show episodes",
    prompt:
      "Help me find relevant episodes. Ask what filters I want, then confirm before changing the page or applying filters.",
  },
];

function CapabilityChips({
  onStartConversation,
}: {
  onStartConversation: (prompt: string) => void;
}) {
  return (
    <div className={styles.capabilityChips} aria-label="Suggested prompts">
      {CAPABILITY_PROMPTS.map((item) => (
        <button
          key={item.label}
          type="button"
          className={styles.capabilityChip}
          onClick={() => onStartConversation(item.prompt)}
        >
          {item.label}
        </button>
      ))}
    </div>
  );
}

export function WebAssistantWorkspace({
  sessionId,
  initialMessage,
}: WebAssistantWorkspaceProps) {
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const trimmed = sessionId?.trim() || null;
    mainChatSessionIdRef.current = trimmed;
    return () => {
      mainChatSessionIdRef.current = null;
    };
  }, [sessionId]);
  const dashboardAssistantContext = useDashboardAssistantContextStore((s) => s.context);
  const { data: conversations = [], isLoading: isLoadingConversations } = useConversations();
  const deleteConversation = useDeleteConversation();

  const getAppState = useCallback(
    (): Record<string, unknown> => ({
      ...buildAssistantAppState({
        activeTab: routeToTabId(location.pathname),
        workspacePath: "",
        platform: "web",
        episodeFilters: location.pathname.startsWith("/episodes")
          ? {
              ...readEpisodeFiltersFromSearch(location.search),
              currentPage: useEpisodeFiltersStore.getState().currentPage,
            }
          : flattenEpisodeFiltersForAssistant(
              useEpisodeFiltersStore.getState().filters,
              {
                viewMode: useEpisodeDisplayStore.getState().viewMode,
                sort: useEpisodeDisplayStore.getState().sort,
                pageSize: useEpisodeDisplayStore.getState().pageSize,
              },
              useEpisodeFiltersStore.getState().currentPage,
            ),
        workflowFilters: readWorkflowFiltersFromLocation(
          location.pathname,
          location.search,
        ),
        chatSurface: "main",
        dashboardContext: dashboardAssistantContext ?? undefined,
      }),
    }),
    [location.pathname, location.search, dashboardAssistantContext],
  );

  const {
    messages,
    isStreaming,
    error,
    sendMessage,
    stopStreaming,
    clearMessages,
    dashboardProposal,
    streamingWidgets,
    clearDashboardArtifact,
    confirmToolCall,
    cancelToolCall,
    answerToolCall,
  } = useChat(getAppState);

  // Re-shape the loose chat-store proposal into the rich type the
  // dashboards package expects. The store keeps it loose so the
  // assistant package doesn't have to depend on dashboards.
  const richProposal = useMemo(
    () =>
      dashboardProposal
        ? {
            title: dashboardProposal.title,
            description: dashboardProposal.description ?? null,
            definition: dashboardProposal.definition as DashboardDefinition,
            explanation: dashboardProposal.explanation,
            ...(typeof dashboardProposal.is_public === "boolean"
              ? { is_public: dashboardProposal.is_public }
              : {}),
          }
        : null,
    [dashboardProposal],
  );

  // Same conversion for the streaming overlay — the store keeps the
  // widget objects opaque so the assistant package doesn't pull in
  // recharts / table / etc. transitively.
  const richStreamingWidgets = useMemo(
    () => streamingWidgets as Map<number, DashboardWidget>,
    [streamingWidgets],
  );

  const showArtifact = richProposal !== null || richStreamingWidgets.size > 0;

  const handleDashboardSaved = useCallback(
    (saved: Dashboard) => {
      clearDashboardArtifact();
      navigate(`/dashboards/${saved.id}`);
    },
    [clearDashboardArtifact, navigate],
  );

  const artifactPanel = showArtifact ? (
    <DashboardArtifactPanel
      proposal={richProposal}
      streamingWidgets={richStreamingWidgets}
      onSaved={handleDashboardSaved}
      onDismiss={clearDashboardArtifact}
    />
  ) : undefined;

  useConversationSession({
    sessionId,
    initialMessage,
    sendMessage,
  });

  const selectedConversation = useMemo(
    () =>
      sessionId
        ? conversations.find((conversation) => conversation.session_id === sessionId) ?? null
        : null,
    [conversations, sessionId],
  );

  const firstUserMessage = useMemo(
    () => messages.find((message) => message.role === "user"),
    [messages],
  );

  const activeConversationTitle =
    selectedConversation?.title ??
    (firstUserMessage
      ? `${firstUserMessage.content.slice(0, 72)}${firstUserMessage.content.length > 72 ? "…" : ""}`
      : "New conversation");

  const welcomeTitle = "How can I help?";

  const handleStartConversation = useCallback(
    (prompt: string) => {
      if (sessionId) {
        sendMessage(prompt);
        return;
      }
      const nextSessionId = crypto.randomUUID();
      navigate(`/chat/${nextSessionId}`, { state: { initialMessage: prompt } });
    },
    [navigate, sendMessage, sessionId],
  );

  const handleSuggestedPrompt = useCallback(
    (prompt: string) => {
      if (sessionId) {
        sendMessage(prompt);
      } else {
        handleStartConversation(prompt);
      }
    },
    [handleStartConversation, sendMessage, sessionId],
  );

  const handleSelectConversation = useCallback(
    (conversation: (typeof conversations)[number]) => {
      stopStreaming();
      navigate(`/chat/${conversation.session_id}`);
    },
    [navigate, stopStreaming],
  );

  const handleNewConversation = useCallback(() => {
    stopStreaming();
    clearMessages();
    navigate("/");
  }, [clearMessages, navigate, stopStreaming]);

  const handleDeleteConversation = useCallback(
    (deletedSessionId: string) => {
      stopStreaming();
      clearMessages();
      deleteConversation.mutate(deletedSessionId);
      navigate("/");
    },
    [clearMessages, deleteConversation, navigate, stopStreaming],
  );

  return (
    <AssistantWorkspace
      conversations={conversations}
      isLoadingConversations={isLoadingConversations}
      selectedSessionId={sessionId}
      onSelectConversation={handleSelectConversation}
      onNewConversation={handleNewConversation}
      onStartConversation={handleStartConversation}
      onDeleteConversation={handleDeleteConversation}
      messages={messages}
      isStreaming={isStreaming}
      error={error}
      onSendMessage={sendMessage}
      onStopStreaming={stopStreaming}
      onConfirmToolCall={confirmToolCall}
      onCancelToolCall={cancelToolCall}
      onAnswerToolCall={answerToolCall}
      welcomeEyebrow="Parametric"
      welcomeTitle={welcomeTitle}
      welcomeDescription=""
      welcomeNote=""
      welcomePlaceholder="Ask Gauss anything..."
      welcomeSupplementaryContent={
        <CapabilityChips onStartConversation={handleSuggestedPrompt} />
      }
      activeConversationTitle={activeConversationTitle}
      activeConversationSummary={selectedConversation?.summary}
      chatPlaceholder="Continue the conversation…"
      emptyStateTitle="New conversation"
      emptyStateDescription="Ask Gauss about your data, your workflows, or the next action you want to take."
      rightPanel={artifactPanel}
    />
  );
}
